import { Sun, Book, Users, Quote } from "lucide-react";
import Location from "../components/Location/Location";
import { useTranslation } from "react-i18next";
import { Heart, Leaf, Home, Crown, Building, Megaphone, Target, TreePine } from "lucide-react";

const About = () => {
  const { t } = useTranslation();

  return (
    <>
      {/* Simple Hero Banner - No Parallax */}
      <div id="aboutTop" className="relative h-[60vh] overflow-hidden flex items-center justify-center">
        {/* Background Image */}
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: "",
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />

        {/* Red Overlay - Semi-transparent */}
        <div className="absolute inset-0 bg-[#7B0000] z-10"></div>

        {/* Content */}
        <div className="relative z-20 text-center text-white px-4">
          <h1 className="text-3xl md:text-6xl font-serif mb-4">{t("about.hero.title")}</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
            {t("about.hero.subtitle")}
          </p>
          <div className="mt-8 inline-block">
            <div className="h-0.5 w-20 bg-white/70 mx-auto"></div>
            <div className="h-0.5 w-12 bg-white/50 mx-auto mt-1"></div>
            <div className="h-0.5 w-6 bg-white/30 mx-auto mt-1"></div>
          </div>
        </div>

        {/* Decorative Om Symbol */}
        <div className="absolute bottom-4 right-4 text-white/20 text-6xl font-serif z-10">ॐ</div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        {/* About Section */}
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-8">
            <div className="w-1.5 h-12 bg-gradient-to-b from-[#B82A1E] to-[#8a1f16] mr-4 rounded-full"></div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#5D4037]">
              {t("about.temple.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center mb-16">
            <div>
              <p className="text-[#5D4037]/80 leading-relaxed mb-4">
                {t("about.temple.paragraph1")}
              </p>
              <p className="text-[#5D4037]/80 leading-relaxed mb-4">
                {t("about.temple.paragraph2")}
              </p>
              <p className="text-[#5D4037]/80 leading-relaxed">{t("about.temple.paragraph3")}</p>
            </div>
            <div className="relative">
              <div className="relative h-80 w-full overflow-hidden rounded-lg shadow-lg">
                <img
                  src="/assets/Temple.jpg"
                  alt={t("about.temple.imageAlt")}
                  className="object-cover w-full h-full"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 h-24 w-24 border-4 border-[#B82A1E]/20 rounded-lg -z-10"></div>
              <div className="absolute -top-4 -left-4 h-24 w-24 border-4 border-[#B82A1E]/20 rounded-lg -z-10"></div>
            </div>
          </div>
        </div>

        {/* Mission & Values */}
        <div className="bg-[#fcf9f5] rounded-2xl p-8 md:p-12 my-16 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 text-[#B82A1E]/5 text-[200px] font-serif">
            ॐ
          </div>

          <div className="max-w-4xl mx-auto relative z-10">
            <div className="flex items-center mb-8">
              <div className="w-1.5 h-12 bg-gradient-to-b from-[#B82A1E] to-[#8a1f16] mr-4 rounded-full"></div>
              <h2 className="text-3xl md:text-4xl font-serif text-[#5D4037]">
                {t("about.mission.sectionTitle")}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              <div>
                <h3 className="text-2xl font-serif text-[#5D4037] mb-4">
                  {t("about.mission.title")}
                </h3>
                <p className="text-[#5D4037]/80 leading-relaxed mb-4">
                  {t("about.mission.paragraph1")}
                </p>
                <p className="text-[#5D4037]/80 leading-relaxed">{t("about.mission.paragraph2")}</p>
              </div>

              <div>
                <h3 className="text-2xl font-serif text-[#5D4037] mb-4">
                  {t("about.values.title")}
                </h3>
                <ul className="space-y-4">
                  {[
                    {
                      icon: <Heart className="text-[#B82A1E]" size={20} />,
                      text: t("about.values.compassion"),
                    },
                    {
                      icon: <Sun className="text-[#B82A1E]" size={20} />,
                      text: t("about.values.enlightenment"),
                    },
                    {
                      icon: <Book className="text-[#B82A1E]" size={20} />,
                      text: t("about.values.wisdom"),
                    },
                    {
                      icon: <Users className="text-[#B82A1E]" size={20} />,
                      text: t("about.values.community"),
                    },
                  ].map((value, index) => (
                    <li key={index} className="flex items-start">
                      <div className="mr-3 mt-1">{value.icon}</div>
                      <span className="text-[#5D4037]/80">{value.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Key Focus Areas Section */}
        <section className="relative py-24 overflow-hidden bg-gradient-to-b from-[#fcf9f5] to-white">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#B82A1E]/0 via-[#B82A1E]/30 to-[#B82A1E]/0"></div>

          {/* Mandala-inspired decorative circles */}
          <div className="absolute -top-32 -right-32 w-64 h-64 border border-[#B82A1E]/5 rounded-full"></div>
          <div className="absolute -top-24 -right-24 w-48 h-48 border border-[#B82A1E]/5 rounded-full"></div>
          <div className="absolute -top-16 -right-16 w-32 h-32 border border-[#B82A1E]/5 rounded-full"></div>

          <div className="absolute -bottom-32 -left-32 w-64 h-64 border border-[#B82A1E]/5 rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-48 h-48 border border-[#B82A1E]/5 rounded-full"></div>
          <div className="absolute -bottom-16 -left-16 w-32 h-32 border border-[#B82A1E]/5 rounded-full"></div>

          {/* Om symbol watermark */}
          <div className="absolute opacity-[0.02] text-[#B82A1E] text-[400px] font-serif top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none">
            ॐ
          </div>

          <div className="container relative z-10 px-5 mx-auto">
            {/* Section Header */}
            <div className="mb-16 text-center">
              {/* <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-gradient-to-br from-[#B82A1E]/10 to-[#B82A1E]/5">
                <span className="text-[#B82A1E] text-2xl">🎯</span>
              </div> */}

              <h2 className="relative inline-block mb-6 text-4xl font-serif text-[#5D4037] after:content-[''] after:absolute after:w-full after:h-0.5 after:bg-gradient-to-r after:from-transparent after:via-[#B82A1E] after:to-transparent after:bottom-0 after:left-0">
                {t("about.keyFocusAreas.title")}
              </h2>

              <p className="max-w-3xl mx-auto text-center leading-relaxed text-[#5D4037]/80">
                {t("about.keyFocusAreas.subtitle")}
              </p>
            </div>

            {/* Focus Areas List */}
            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {[
                  {
                    icon: <Heart className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.spiritualWisdom.title"),
                    description: t("about.keyFocusAreas.areas.spiritualWisdom.description"),
                  },
                  {
                    icon: <Leaf className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.sustainableLiving.title"),
                    description: t("about.keyFocusAreas.areas.sustainableLiving.description"),
                  },
                  {
                    icon: <Users className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.marginalizedCommunities.title"),
                    description: t("about.keyFocusAreas.areas.marginalizedCommunities.description"),
                  },
                  {
                    icon: <Home className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.welfareServices.title"),
                    description: t("about.keyFocusAreas.areas.welfareServices.description"),
                  },
                  {
                    icon: <Crown className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.womenEmpowerment.title"),
                    description: t("about.keyFocusAreas.areas.womenEmpowerment.description"),
                  },
                  {
                    icon: <Building className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.infrastructure.title"),
                    description: t("about.keyFocusAreas.areas.infrastructure.description"),
                  },
                  {
                    icon: <Megaphone className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.awareness.title"),
                    description: t("about.keyFocusAreas.areas.awareness.description"),
                  },
                  {
                    icon: <Target className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.developmentGoals.title"),
                    description: t("about.keyFocusAreas.areas.developmentGoals.description"),
                  },
                  {
                    icon: <TreePine className="text-[#B82A1E]" size={24} />,
                    title: t("about.keyFocusAreas.areas.environment.title"),
                    description: t("about.keyFocusAreas.areas.environment.description"),
                  },
                ].map((area, index) => (
                  <div
                    key={index}
                    className="flex items-start p-6 bg-white rounded-xl shadow-md border border-[#B82A1E]/10"
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 mr-6 rounded-full bg-gradient-to-br from-[#B82A1E]/10 to-[#B82A1E]/5">
                      {area.icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-xl font-serif text-[#5D4037] mb-3">{area.title}</h3>
                      <p className="text-[#5D4037]/80 leading-relaxed">{area.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Spiritual Journey Timeline */}
        <div className="max-w-4xl mx-auto my-16">
          <div className="flex items-center mb-12">
            <div className="w-1.5 h-12 bg-gradient-to-b from-[#B82A1E] to-[#8a1f16] mr-4 rounded-full"></div>
            <h2 className="text-3xl md:text-4xl font-serif text-[#5D4037]">
              {t("about.journey.title")}
            </h2>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 md:left-1/2 top-7 bottom-0 w-0.5 bg-[#B82A1E]/20 transform md:translate-x-px"></div>

            {/* Timeline events */}
            {[
              {
                year: "1978",
                title: t("about.journey.events.foundation.title"),
                description: t("about.journey.events.foundation.description"),
              },
              {
                year: "1985",
                title: t("about.journey.events.ashram.title"),
                description: t("about.journey.events.ashram.description"),
              },
              {
                year: "1997",
                title: t("about.journey.events.global.title"),
                description: t("about.journey.events.global.description"),
              },
              {
                year: "2010",
                title: t("about.journey.events.revival.title"),
                description: t("about.journey.events.revival.description"),
              },
              {
                year: t("about.journey.events.present.year"),
                title: t("about.journey.events.present.title"),
                description: t("about.journey.events.present.description"),
              },
            ].map((event, index) => (
              <div
                key={index}
                className={`relative flex flex-col md:flex-row items-center md:items-start gap-8 mb-12 ${
                  index % 2 === 0 ? "md:flex-row-reverse" : ""
                }`}
              >
                <div className="md:w-1/2 text-center md:text-right md:pr-8 md:pl-0 pl-8">
                  <div className={`${index % 2 === 0 ? "md:text-left md:pl-8 md:pr-0" : ""}`}>
                    <span className="inline-block px-3 py-1 bg-[#B82A1E]/10 text-[#B82A1E] rounded-full text-sm font-medium mb-2">
                      {event.year}
                    </span>
                    <h3 className="text-xl font-serif text-[#5D4037] mb-2">{event.title}</h3>
                    <p className="text-[#5D4037]/70">{event.description}</p>
                  </div>
                </div>

                <div className="absolute left-5 md:left-1/2 top-7 w-8 h-8 bg-white border-4 border-[#B82A1E] rounded-full transform -translate-x-1/2 md:-translate-y-4 flex items-center justify-center">
                  <div className="w-2 h-2 bg-[#B82A1E] rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial/Quote Section */}
        <div className="relative my-16 py-16 overflow-hidden">
          <div className="absolute inset-0 bg-[#B82A1E]/5 -skew-y-2"></div>
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <Quote className="mx-auto text-[#B82A1E]/30 mb-6" size={48} />
            <blockquote className="text-2xl md:text-3xl font-serif text-[#5D4037] italic mb-6">
              {t("about.quotee.text")}
            </blockquote>
            <div className="h-0.5 w-20 bg-[#B82A1E]/30 mx-auto mb-4"></div>
            <p className="text-[#5D4037] font-medium">{t("about.quotee.author")}</p>
            {/* <p className="text-[#5D4037]/70 text-sm">{t("about.quotee.role")}</p> */}
          </div>
        </div>
      </div>

      {/* Location Component */}
      <Location />
    </>
  );
};

export default About;
